import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import { ChevronRight, Search } from "lucide-react";
import { getCategory, liveToolsByCategory, toolsByCategory, categories } from "@/lib/toolsData";
import { ToolCard } from "@/components/ToolCard";
import { BannerAd } from "@/components/AdSlots";
import { useSeo } from "@/lib/useSeo";

const PAGE = 24;

const Category = () => {
  const { id } = useParams();
  const category = getCategory(id);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("default");
  const [limit, setLimit] = useState(PAGE);

  useSeo({
    title: category ? `${category.name} — Free Online ${category.name}` : "Category",
    description: category ? category.description : "",
    path: `/category/${id}`,
    jsonLd: category ? { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
      { "@type": "ListItem", position: 2, name: category.name, item: `${window.location.origin}/category/${id}` },
    ] } : null,
  });

  const list = useMemo(() => {
    if (!category) return [];
    let arr = liveToolsByCategory(id);
    if (q.trim()) arr = arr.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()));
    if (sort === "az") arr = [...arr].sort((a, b) => a.name.localeCompare(b.name));
    return arr;
  }, [category, id, q, sort]);

  if (!category) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold">Category not found</h1>
        <Link to="/" className="mt-4 inline-block text-primary hover:underline">← Back home</Link>
      </div>
    );
  }

  const Icon = category.icon;
  const shown = list.slice(0, limit);
  const liveCount = liveToolsByCategory(id).length;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 animate-fade-up">
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="font-medium text-foreground">{category.name}</span>
      </nav>

      <div className="mt-6 flex items-center gap-4">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ backgroundColor: category.accent }}>
          <Icon className="h-7 w-7" />
        </span>
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight sm:text-4xl">{category.name}</h1>
          <p className="text-sm text-muted-foreground">{liveCount} production-ready tools</p>
        </div>
      </div>
      <p className="mt-3 max-w-2xl text-muted-foreground">{category.description}</p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-1 items-center rounded-xl border border-border bg-card px-3 focus-within:border-primary">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input data-testid="category-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${category.name}…`} className="w-full bg-transparent px-3 py-2.5 text-sm outline-none" />
        </div>
        <select data-testid="category-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-xl border border-border bg-card px-3 py-2.5 text-sm outline-none">
          <option value="default">Sort: Popular</option>
          <option value="az">Sort: A–Z</option>
        </select>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((t) => <ToolCard key={t.slug} tool={t} />)}
      </div>

      {limit < list.length && (
        <div className="mt-8 text-center">
          <button data-testid="load-more" onClick={() => setLimit((l) => l + PAGE)} className="rounded-lg border border-border bg-card px-6 py-2.5 text-sm font-semibold transition-colors hover:bg-muted">
            Load more ({list.length - limit} left)
          </button>
        </div>
      )}

      <div className="my-12"><BannerAd /></div>

      <div>
        <p className="overline text-muted-foreground">Other categories</p>
        <div className="mt-4 flex flex-wrap gap-3">
          {categories.filter((c) => c.id !== id).map((c) => (
            <Link key={c.id} to={`/category/${c.id}`} className="rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-muted">{c.name}</Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Category;
